import type { FirebaseAuthService } from '~common/google-auth/services/firebase-auth'
import type { Functions } from 'firebase/functions'
import type { AutoFetchConfig } from '~common/storage/auto-fetch-config' // assume path
import { httpsCallable } from 'firebase/functions'

export const EXTENSION_ID_PROD = 'jefjpdaliglhlppaleecfhfomioifcpp'

interface Message {
    type: string
    [key: string]: unknown
}

interface Deps {
    webExtensionApi: typeof chrome
    extensionId?: string
    auth: FirebaseAuthService
    _functions: Functions
}

export class ExtensionMessagingService {
    private port: chrome.runtime.Port | null = null
    private readonly extensionId: string

    constructor(private readonly deps: Deps) {
        this.extensionId = deps.extensionId ?? EXTENSION_ID_PROD

        // Listen for extension installation message and trigger reconnection
        if (typeof window !== 'undefined') {
            window.addEventListener('message', (event: MessageEvent) => {
                // Only accept messages from same origin (security)
                if (event.origin !== window.location.origin) {
                    return
                }

                const message = event.data as {
                    source?: string
                    type?: string
                    id?: string
                    timestamp?: number
                } | null

                if (
                    message?.source === 'taxing-extension' &&
                    message?.type === 'extension-installed'
                ) {
                    // Connect to the extension
                    this.connect()
                }

                // Handle auth request from bridge (Firefox fallback)
                if (
                    message?.source === 'taxing-extension' &&
                    message?.type === 'request-auth-token'
                ) {
                    this.handleAuthRequest(message.id, true).catch((error) => {
                        console.error(
                            '[ExtensionMessaging] Bridge auth request failed:',
                            error,
                        )
                    })
                }
            })
        }
    }

    connect(): void {
        if (this.port) {
            console.warn(
                '[ExtensionMessaging] Already connected, skipping connect()',
            )
            return
        }
        if (
            typeof this.deps.webExtensionApi === 'undefined' ||
            !this.deps.webExtensionApi?.runtime
        ) {
            console.warn(
                '[ExtensionMessaging] Chrome extension API not available',
                {
                    chromeDefined:
                        typeof this.deps.webExtensionApi !== 'undefined',
                    chromeRuntime:
                        typeof this.deps.webExtensionApi?.runtime !==
                        'undefined',
                },
            )
            return
        }

        try {
            this.port = this.deps.webExtensionApi.runtime.connect(
                this.extensionId,
                {
                    name: 'taxing-webapp-port',
                },
            )

            this.port.onMessage.addListener((message: Message) => {
                this.handleIncomingMessage(message)
            })

            this.port.onDisconnect.addListener(() => {
                const error = this.deps.webExtensionApi.runtime.lastError
                console.warn(
                    '[ExtensionMessaging] Port disconnected',
                    error
                        ? {
                              error: error.message,
                          }
                        : {},
                )
                this.port = null
            })

            // Check for connection errors asynchronously
            // Note: chrome.runtime.lastError must be checked asynchronously
            setTimeout(() => {
                const error = this.deps.webExtensionApi.runtime.lastError
                if (error) {
                    console.error(
                        '[ExtensionMessaging] Connection error (async check):',
                        error.message,
                    )
                    if (this.port) {
                        this.port = null
                    }
                } else {
                    console.warn(
                        '[ExtensionMessaging] Connection established successfully',
                    )
                }
            }, 0)
        } catch (error) {
            console.error('[ExtensionMessaging] Failed to connect:', error)
            this.port = null
            throw error
        }
    }

    isExtensionInstalled(): Promise<boolean> {
        return new Promise((resolve) => {
            try {
                const testPort = this.deps.webExtensionApi.runtime.connect(
                    this.extensionId,
                    {
                        name: 'taxing-webapp-port',
                    },
                )
                let resolved = false

                const cleanup = () => {
                    if (!resolved) {
                        resolved = true
                        try {
                            testPort.onDisconnect.removeListener(onDisconnect)
                            testPort.onMessage.removeListener(onMessage)
                        } catch {
                            // Ignore cleanup errors
                        }
                    }
                }

                const onDisconnect = () => {
                    if (this.deps.webExtensionApi.runtime.lastError) {
                        cleanup()
                        resolve(false)
                    } else {
                        // Connection succeeded but was disconnected
                        if (!resolved) {
                            resolved = true
                            testPort.onDisconnect.removeListener(onDisconnect)
                            testPort.onMessage.removeListener(onMessage)
                            resolve(true)
                        }
                    }
                }

                const onMessage = (message: unknown) => {
                    const msg = message as { type: string; tabId?: number }
                    if (msg.type === 'pong') {
                        cleanup()
                        resolve(true)
                    }
                }

                testPort.onDisconnect.addListener(onDisconnect)
                testPort.onMessage.addListener(onMessage)

                // Try to send a test message - if extension is installed, this won't error
                try {
                    testPort.postMessage({ type: 'ping' })
                    // Timeout after 1 second if no pong response
                    setTimeout(() => {
                        if (!resolved) {
                            cleanup()
                            resolve(false)
                        }
                    }, 1000)
                } catch {
                    cleanup()
                    resolve(false)
                }
            } catch {
                resolve(false)
            }
        })
    }

    async checkExtensionInstalled(
        timeoutMs: number = 30000,
        pollIntervalMs: number = 1500,
    ): Promise<boolean> {
        const startTime = Date.now()
        while (Date.now() - startTime < timeoutMs) {
            const isInstalled = await this.isExtensionInstalled()
            if (isInstalled) {
                return true
            }
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
        }
        return false
    }

    private async handleIncomingMessage(message: Message): Promise<void> {
        console.warn('handleIncomingMessage', message)
        switch (message.type) {
            case 'request-auth-token':
                await this.handleAuthRequest(
                    message.id as string | undefined,
                    false,
                )
                break
            case 'check-auth-status-response':
            case 'tab-activation-result':
            case 'auth-token-response':
            case 'pong':
                // These are response messages handled by their specific request handlers
                break
            // add other types if needed
            default:
                console.warn(
                    '[ExtensionMessaging] Unknown message type:',
                    message.type,
                )
        }
    }

    private async handleAuthRequest(
        id?: string,
        isBridge: boolean = false,
    ): Promise<void> {
        try {
            const customToken = await this.getCustomTokenForExtension()
            const response = {
                type: 'auth-token-response',
                id: id,
                token: customToken,
                user: {
                    uid: (await this.deps.auth.getLoggedInUser())?.uid,
                },
            }

            if (this.port && !isBridge) {
                this.port.postMessage(response)
            } else {
                // Fallback for Firefox bridge
                window.postMessage(
                    {
                        source: 'taxing-webapp',
                        ...response,
                    },
                    window.location.origin,
                )
            }
        } catch (error) {
            console.error(
                '[ExtensionMessaging] Failed to generate custom token:',
                error,
            )
            const errorResponse = {
                type: 'auth-token-response',
                id: id,
                error: 'Failed to generate token',
            }
            if (this.port && !isBridge) {
                this.port.postMessage(errorResponse)
            } else {
                window.postMessage(
                    {
                        source: 'taxing-webapp',
                        ...errorResponse,
                    },
                    window.location.origin,
                )
            }
        }
    }

    async handleShortcutClick(
        shortcutId: string,
        url: string,
        timestamp: number,
    ): Promise<{ ok: boolean; error?: string }> {
        if (!this.port) {
            this.connect()
            if (!this.port) {
                return { ok: false, error: 'Not connected to extension' }
            }
        }
        const currentPort = this.port
        const id =
            Date.now().toString() + Math.random().toString(36).substr(2, 9)

        return new Promise((resolve) => {
            const listener = (msg: unknown) => {
                const message = msg as {
                    type: string
                    id?: string
                    ok?: boolean
                    error?: string
                }
                if (
                    message.type === 'handle-shortcut-click-response' &&
                    message.id === id
                ) {
                    currentPort.onMessage.removeListener(listener)
                    resolve({
                        ok: message.ok ?? false,
                        error: message.error,
                    })
                }
            }
            currentPort.onMessage.addListener(listener)
            currentPort.postMessage({
                type: 'handle-shortcut-click',
                id,
                shortcutId,
                url,
                lastActiveTimestamp: timestamp,
            })
            setTimeout(() => {
                currentPort.onMessage.removeListener(listener)
                resolve({ ok: false, error: 'Timeout waiting for response' })
            }, 30000)
        })
    }

    private async getCustomTokenForExtension(): Promise<string> {
        const createToken = httpsCallable(
            this.deps._functions,
            'createCustomTokenForExtension',
        )
        const result = (await createToken()) as {
            data: { customToken: string }
        }
        console.warn('createToken result', result)
        return result.data.customToken
    }

    async startAgent(
        config: AutoFetchConfig,
    ): Promise<{ ok: boolean; error?: string }> {
        if (!this.port) {
            this.connect() // Ensure connected
            if (!this.port) {
                throw new Error('Failed to connect to extension')
            }
        }
        const currentPort = this.port
        const id =
            Date.now().toString() + Math.random().toString(36).substr(2, 9)
        if (this.extensionId.includes('PLACEHOLDER')) {
            console.warn(
                '[ExtensionMessaging] WARNING: Using placeholder extensionId! Set VITE_EXTENSION_ID in .env to the actual ID from chrome://extensions.',
            )
        }
        return new Promise((resolve, reject) => {
            const listener = (msg: unknown) => {
                const message = msg as {
                    type: string
                    id: string
                    ok?: boolean
                    error?: string
                }
                if (message.type === 'agent-completed' && message.id === id) {
                    currentPort.onMessage.removeListener(listener)
                    if (message.ok === true) {
                        resolve({ ok: true })
                    } else if (message.ok === false) {
                        reject(new Error(message.error || 'Agent failed'))
                    } else {
                        reject(new Error('Invalid response'))
                    }
                }
            }
            currentPort.onMessage.addListener(listener)
            currentPort.postMessage({
                type: 'start-agent',
                config: config,
                id: id,
            })
            setTimeout(() => {
                currentPort.onMessage.removeListener(listener)
                reject(new Error('Timeout waiting for agent completion'))
            }, 300000) // 5 minutes
        })
    }

    async cancelAgent(): Promise<{ ok: boolean; error?: string }> {
        // Prefer using the persistent port if connected, otherwise fall back to runtime message
        if (this.port) {
            const currentPort = this.port
            const id =
                Date.now().toString() + Math.random().toString(36).substr(2, 9)
            return new Promise((resolve) => {
                const listener = (msg: unknown) => {
                    const message = msg as {
                        type: string
                        id?: string
                        ok?: boolean
                        error?: string
                    }
                    if (
                        message.type === 'agent-cancelled' &&
                        message.id === id
                    ) {
                        currentPort.onMessage.removeListener(listener)
                        if (message.ok === true) {
                            resolve({ ok: true })
                        } else {
                            resolve({
                                ok: false,
                                error: message.error || 'Cancel failed',
                            })
                        }
                    }
                }
                currentPort.onMessage.addListener(listener)
                currentPort.postMessage({ type: 'cancel-agent', id })
                setTimeout(() => {
                    currentPort.onMessage.removeListener(listener)
                    resolve({ ok: false, error: 'Cancel timeout' })
                }, 30000)
            })
        }

        return new Promise((resolve) => {
            this.deps.webExtensionApi.runtime.sendMessage(
                this.extensionId,
                { type: 'cancel-agent' },
                (response?: unknown) => {
                    const res =
                        (response as { ok?: boolean; error?: string }) || {}
                    if (this.deps.webExtensionApi.runtime.lastError) {
                        resolve({
                            ok: false,
                            error: this.deps.webExtensionApi.runtime.lastError
                                .message,
                        })
                        return
                    }
                    resolve({ ok: !!res.ok, error: res.error })
                },
            )
        })
    }

    async getAgentStatus(): Promise<{
        ok: boolean
        running: boolean
        recipeId?: string
    }> {
        return new Promise((resolve) => {
            this.deps.webExtensionApi.runtime.sendMessage(
                this.extensionId,
                { type: 'get-agent-status' },
                (response?: unknown) => {
                    if (this.deps.webExtensionApi.runtime.lastError) {
                        resolve({ ok: false, running: false })
                        return
                    }
                    const res = (response as
                        | { ok?: boolean; running?: boolean; recipeId?: string }
                        | undefined) || { ok: false }
                    resolve({
                        ok: !!res.ok,
                        running: !!res.running,
                        recipeId: res.recipeId,
                    })
                },
            )
        })
    }

    disconnect(): void {
        if (this.port) {
            this.port.disconnect()
            this.port = null
        }
    }
    async checkAuthStatus(): Promise<{
        ok: boolean
        isAuthenticated?: boolean
        error?: string
    }> {
        console.warn('[ExtensionMessaging] checkAuthStatus() method called')
        if (!this.port) {
            this.connect()
            if (!this.port) {
                return { ok: false, error: 'Not connected to extension' }
            }
        }
        const currentPort = this.port
        const startTime = Date.now()
        const timeoutMs = 20000
        const pollIntervalMs = 500

        return new Promise((resolve) => {
            const pendingRequests = new Set<string>()
            const listener = (msg: unknown) => {
                const message = msg as {
                    type: string
                    id?: string
                    ok?: boolean
                    isAuthenticated?: boolean
                    error?: string
                }
                if (
                    message.type === 'check-auth-status-response' &&
                    message.id &&
                    pendingRequests.has(message.id)
                ) {
                    pendingRequests.delete(message.id)
                    if (message.ok && message.isAuthenticated) {
                        clearInterval(intervalId)
                        clearTimeout(timeoutId)
                        currentPort.onMessage.removeListener(listener)
                        resolve({
                            ok: true,
                            isAuthenticated: true,
                        })
                    }
                }
            }
            currentPort.onMessage.addListener(listener)

            const sendCheck = () => {
                if (Date.now() - startTime >= timeoutMs) {
                    return
                }
                const id =
                    Date.now().toString() +
                    Math.random().toString(36).substr(2, 9)
                pendingRequests.add(id)
                const response = currentPort.postMessage({
                    type: 'check-auth-status',
                    id,
                })

                console.warn('response', response)
            }

            // Send first check immediately
            sendCheck()

            // Poll every 500ms
            const intervalId = setInterval(() => {
                sendCheck()
            }, pollIntervalMs)

            // Timeout after 20 seconds
            const timeoutId = setTimeout(() => {
                clearInterval(intervalId)
                currentPort.onMessage.removeListener(listener)
                resolve({
                    ok: false,
                    error: 'Timeout waiting for auth status',
                })
            }, timeoutMs)
        })
    }

    async requestAuthToken(): Promise<{
        ok: boolean
        token?: string
        error?: string
    }> {
        if (!this.port) {
            this.connect()
            if (!this.port) {
                return { ok: false, error: 'Not connected to extension' }
            }
        }
        const currentPort = this.port
        const id =
            Date.now().toString() + Math.random().toString(36).substr(2, 9)

        return new Promise((resolve) => {
            const listener = (msg: unknown) => {
                const message = msg as {
                    type: string
                    id?: string
                    ok?: boolean
                    token?: string
                    error?: string
                }
                if (
                    message.type === 'auth-token-response' &&
                    message.id === id
                ) {
                    currentPort.onMessage.removeListener(listener)
                    resolve({
                        ok: message.ok ?? false,
                        token: message.token,
                        error: message.error,
                    })
                }
            }
            currentPort.onMessage.addListener(listener)
            currentPort.postMessage({
                type: 'request-auth-token',
                id,
            })
            setTimeout(() => {
                currentPort.onMessage.removeListener(listener)
                resolve({ ok: false, error: 'Timeout waiting for auth token' })
            }, 30000)
        })
    }
}
